/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable valid-jsdoc */
import Controller from "sap/ui/core/mvc/Controller";
import UIComponent from "sap/ui/core/UIComponent";
import Component from "../Component";
import Model from "sap/ui/model/Model";
import ResourceModel from "sap/ui/model/resource/ResourceModel";
import ResourceBundle from "sap/base/i18n/ResourceBundle";
import Router from "sap/ui/core/routing/Router";
import History from "sap/ui/core/routing/History";
import Theming from "sap/ui/core/Theming";
import Image from "sap/m/Image";
import Button from "sap/m/Button";
import MessageToast from "sap/m/MessageToast";
import JSONModel from "sap/ui/model/json/JSONModel";
import { Route$MatchedEvent } from "sap/ui/core/routing/Route";
import Log from "sap/base/Log";
import ApiService from "../src/api/ApiService";
import SentryService from "../src/services/SentryService";

/**
 * @namespace de.realcore.realseats.controller
 */
export default abstract class BaseController extends Controller {
    /**
     * Convenience method for accessing the component of the controller's view.
     * @returns The component of the controller's view
     */
    public getOwnerComponent(): Component {
        return super.getOwnerComponent() as Component;
    }

    /**
     * Convenience method to get the API service instance.
     * Provides centralized access to all API operations
     * @returns The API service instance
     */
    public getApiService(): ApiService {
        return this.getOwnerComponent().getApiService();
    }

    /**
     * Convenience method to get the components' router instance.
     * @returns The router instance
     */
    public getRouter(): Router {
        return UIComponent.getRouterFor(this);
    }

    /**
     * Convenience method for getting the i18n resource bundle of the component.
     * @returns The i18n resource bundle of the component
     */
    public getResourceBundle(): ResourceBundle | Promise<ResourceBundle> {
        const oModel = this.getOwnerComponent().getModel("i18n") as ResourceModel;
        return oModel.getResourceBundle();
    }

    /**
     * Convenience method to get text from the i18n resource bundle.
     * @param sKey The key for the text
     * @param aParameters Optional parameters for placeholders
     * @returns The localized text
     */
    public getText(sKey: string, aParameters?: any[]): string {
        const oResourceBundle = this.getResourceBundle();
        if (oResourceBundle instanceof Promise) {
            throw new Error("Resource bundle is not yet available");
        }
        return oResourceBundle.getText(sKey, aParameters) || sKey;
    }

    /**
     * Convenience method for setting the view model in every controller of the application.
     * @param oModel The model instance
     * @param [sName] The model name
     * @returns The current base controller instance
     */
    public setModel(oModel: Model, sName?: string): this {
        this.getView()?.setModel(oModel, sName);
        return this;
    }

    /**
     * Convenience method for triggering the navigation to a specific target.
     * @public
     * @param sName Target name
     * @param [oParameters] Navigation parameters
     * @param [bReplace] Defines if the hash should be replaced (no browser history entry) or set (browser history entry)
     */
    public navTo(sName: string, oParameters?: object, bReplace?: boolean): void {
        this.getRouter().navTo(sName, oParameters, undefined, bReplace);
    }

    /**
     * Convenience event handler for navigating back.
     * It there is a history entry we go one step back in the browser history
     * If not, it will replace the current entry of the browser history with the main route.
     */
    public onNavBack(): void {
        const sPreviousHash = History.getInstance().getPreviousHash();
        if (sPreviousHash !== undefined) {
            window.history.go(-1);
        } else {
            this.getRouter().navTo("main", {}, undefined, true);
        }
    }

    public onButtonToggleDarkModePress(): void {
        SentryService.instrumentUIInteraction(
            "button",
            "theme_toggle",
            () => {
                const lightLogoSrc = "realseats/image/RealCore_Group_light.png";
                const darkLogoSrc = "realseats/image/RealCore_Group_dark.png";

                const currentTheme: string = Theming.getTheme();
                const isDarkMode: boolean = currentTheme === "sap_horizon_dark";

                const newTheme: string = isDarkMode ? "sap_horizon" : "sap_horizon_dark";

                // Track theme change in Sentry
                SentryService.addBreadcrumb("Theme changed", "ui", "info", {
                    fromTheme: currentTheme,
                    toTheme: newTheme,
                    isDarkMode: !isDarkMode
                });

                Theming.setTheme(newTheme);
                localStorage.setItem("theme", newTheme); // TODO: add this as a setting inside the db instead of the localstorage

                const imageControl = this.byId("idLogoImage") as Image;
                imageControl.setSrc(isDarkMode ? lightLogoSrc : darkLogoSrc);

                const buttonControl = this.byId("idDarkModeToggleButton") as Button;
                buttonControl.setIcon(
                    isDarkMode ? "sap-icon://dark-mode" : "sap-icon://light-mode"
                );

                // Notify all controllers about theme change via EventBus
                this.getOwnerComponent().getEventBus().publish("App", "ThemeChanged", {
                    newTheme: newTheme,
                    isDarkMode: !isDarkMode
                });
            },
            "dark-mode-toggle",
            { currentTheme: Theming.getTheme() }
        );
    }

    /**
     * Alternative method name for theme toggle (used in footer fragment)
     * Delegates to the main toggle method for consistency
     */
    public onToggleDarkMode(): void {
        this.onButtonToggleDarkModePress();
    }

    /**
     * Generic method to handle route matched events and set the selected data model.
     * @param oEvent The route matched event
     * @param sModelPath The path to the model data
     * @param sRouteParam The route parameter name
     * @param sModelProperty The model property name
     * @param oSelectedModel The selected data model
     */
    protected _onMatchedGeneric(
        oEvent: Route$MatchedEvent,
        sModelPath: string,
        sRouteParam: string,
        sModelProperty: string,
        oSelectedModel: JSONModel
    ): void {
        const oArgs = oEvent.getParameter("arguments") as { [key: string]: string };
        const sParamValue = oArgs[sRouteParam];

        const oModel = new JSONModel();
        void oModel.loadData(sModelPath);
        oModel.attachRequestCompleted(() => {
            const aData = oModel.getProperty("/bookings") || oModel.getProperty("/workspaces");
            if (aData) {
                const aFilteredData = aData.filter(
                    (item: any) => item[sModelProperty] === sParamValue
                );
                oSelectedModel.setData({
                    selectedWorkspace: aFilteredData[0] || {},
                    selectedEmployee: aFilteredData[0] || {},
                    selectedBooking: aFilteredData[0] || {}
                });
            } else {
                Log.error("Data is undefined.");
                oSelectedModel.setData({
                    selectedWorkspace: {},
                    selectedEmployee: {},
                    selectedBooking: {}
                });
            }
        });
    }

    /**
     * Event handler for bug report button.
     * Opens SharePoint bug report form in a new tab.
     */
    public onBug(): void {
        SentryService.instrumentUIInteraction(
            "button",
            "bug_report",
            () => {
                SentryService.addBreadcrumb("User opened bug report form", "navigation", "info", {
                    destination: "sharepoint_bug_report",
                    timestamp: new Date().toISOString()
                });

                window.open(
                    "https://realcorevv.sharepoint.com/sites/RC-Intranet/Lists/RealSeats%20Bugreport/NewForm.aspx",
                    "_blank"
                );
            },
            "bug-report-button"
        );
    }

    /**
     * Enhanced error handling method with Sentry integration
     *
     * @param error The error to handle
     * @param userMessage Optional user-friendly message
     * @param context Additional context information
     */
    protected handleError(error: Error, userMessage?: string, context?: Record<string, any>): void {
        // Capture error in Sentry with context
        SentryService.captureError(error, {
            controller: this.getMetadata().getName(),
            userMessage,
            context,
            timestamp: new Date().toISOString()
        });

        // Log error for debugging
        Log.error(`Error in ${this.getMetadata().getName()}:`, error.message, error.stack);

        // Show user-friendly message if provided
        if (userMessage) {
            MessageToast.show(userMessage);
        }
    }

    /**
     * Execute an async operation with comprehensive error handling
     *
     * @param operationName Name of the operation for logging
     * @param operation Function to execute
     * @param errorMessage Optional error message for user
     * @returns Result of the operation or null if error occurred
     */
    protected async executeWithErrorHandling<T>(
        operationName: string,
        operation: () => Promise<T>,
        errorMessage?: string
    ): Promise<T | null> {
        return SentryService.startSpan("controller.operation", operationName, async (span) => {
            try {
                span?.setAttribute("controller", this.getMetadata().getName());
                span?.setAttribute("operation", operationName);

                const result = await operation();

                SentryService.addBreadcrumb(
                    `Operation completed: ${operationName}`,
                    "controller",
                    "info",
                    {
                        controller: this.getMetadata().getName(),
                        success: true
                    }
                );

                return result;
            } catch (error) {
                span?.setAttribute("error", true);

                this.handleError(
                    error as Error,
                    errorMessage || `Failed to execute ${operationName}`,
                    { operation: operationName }
                );

                return null;
            }
        });
    }
}
